import { Link } from 'react-router-dom'
import { useAnalysisJob } from '../../api/queries/analysisJob'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { JOB_STATUS_LABEL, JOB_STEPS } from './jobSteps'
import styles from './Upload.module.css'

/**
 * What the analysis is doing, while it does it.
 *
 * Steps come from the backend's own progress record — `current_step` and
 * `completed_steps` — not from a sequence invented here, so the display cannot
 * drift from what the pipeline actually runs.
 */
export function AnalysisProgress({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const job = useAnalysisJob(jobId)

  if (job.isPending) {
    return (
      <div className={styles.body}>
        <p className={styles.explain}>Starting the analysis…</p>
      </div>
    )
  }

  if (job.isError) {
    return (
      <div className={styles.body}>
        <p className={styles.error} role="alert">
          The analysis was accepted, but its progress could not be read. {userFacingMessage(job.error)} It is still
          running — reopen the deal in a moment.
        </p>
        <div className={styles.actions}>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    )
  }

  const { status, progress, failure, deal_id: dealIdFromJob } = job.data
  const completed = new Set(progress?.completed_steps ?? [])

  if (status === 'failed') {
    return (
      <div className={styles.body}>
        <p className={styles.error} role="alert">
          {failure?.message ?? 'The analysis failed.'}
        </p>
        {failure?.credit_refunded ? <p className={styles.explain}>Your credit has been returned.</p> : null}
        <div className={styles.actions}>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    )
  }

  if (status === 'completed') {
    return (
      <div className={styles.body}>
        <p className={styles.done} role="status">
          Analysis complete.
        </p>
        <div className={styles.actions}>
          {dealIdFromJob ? (
            <Link className={styles.primaryLink} to={`/deals/${dealIdFromJob}`} onClick={onClose}>
              Open the deal
            </Link>
          ) : null}
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.body}>
      <ol className={styles.steps} aria-label="Analysis progress">
        {JOB_STEPS.map((step) => {
          const state = completed.has(step) ? 'done' : step === status ? 'current' : 'pending'
          return (
            <li key={step} className={`${styles.step} ${styles[state]}`}>
              <span className={styles.stepMark} aria-hidden="true">
                {state === 'done' ? '✓' : state === 'current' ? '•' : '·'}
              </span>
              {JOB_STATUS_LABEL[step]}
            </li>
          )
        })}
      </ol>
      <p className={styles.explain} aria-live="polite">
        {JOB_STATUS_LABEL[status]}. This usually takes a minute or two. You can close this and keep working — the
        deal appears on the board when it is done.
      </p>
      <div className={styles.actions}>
        <Button onClick={onClose}>Close and keep working</Button>
      </div>
    </div>
  )
}
